import { initializePaddle } from "@paddle/paddle-js";
import { useState, useEffect, useCallback } from "react";

const PADDLE_TOKEN = process.env.REACT_APP_PADDLE_TOKEN;
const PADDLE_PRICE_ID = process.env.REACT_APP_PADDLE_PRICE_ID;

export function usePaddleCheckout() {
  const [paddle, setPaddle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!PADDLE_TOKEN) {
      setLoading(false);
      return;
    }

    initializePaddle({
      environment: "production",
      token: PADDLE_TOKEN,
    })
      .then((instance) => {
        if (instance) setPaddle(instance);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Paddle init failed:", err);
        setLoading(false);
      });
  }, []);

  // Overlay (modal) checkout — used by /app/billing for re-subscribe and the
  // sticky Landing CTAs for logged-in non-Pro users.
  const openCheckout = useCallback(
    ({ email, userId, discountCode, onSuccess } = {}) => {
      if (!paddle) return;

      paddle.Checkout.open({
        items: [{ priceId: PADDLE_PRICE_ID, quantity: 1 }],
        customer: email ? { email } : undefined,
        customData: userId ? { user_id: userId } : undefined,
        discountCode: discountCode || undefined,
        settings: {
          displayMode: "overlay",
          theme: "dark",
          locale: "en",
          allowLogout: !email,
          showAddDiscounts: true,
        },
        successCallback: (data) => {
          if (onSuccess) onSuccess(data);
        },
      });
    },
    [paddle]
  );

  // Inline checkout — mounts the Paddle iframe inside a DOM element on our
  // own /app/checkout page so we can wrap it with the outcome guarantee,
  // features list, testimonial, and trust signals. Same Paddle session, just
  // not in a modal — historically lifts checkout completion ~5-8% on mobile
  // because users can't accidentally tap-dismiss it.
  const openInlineCheckout = useCallback(
    ({ frameTarget, email, userId, discountCode, onSuccess } = {}) => {
      if (!paddle || !frameTarget) return;

      paddle.Checkout.open({
        items: [{ priceId: PADDLE_PRICE_ID, quantity: 1 }],
        customer: email ? { email } : undefined,
        customData: userId ? { user_id: userId } : undefined,
        discountCode: discountCode || undefined,
        settings: {
          displayMode: "inline",
          theme: "dark",
          locale: "en",
          allowLogout: false,
          showAddDiscounts: true,
          frameTarget,
          frameInitialHeight: 450,
          frameStyle: "width:100%;min-width:312px;background-color:transparent;border:none;",
        },
        successCallback: (data) => {
          if (onSuccess) onSuccess(data);
        },
      });
    },
    [paddle]
  );

  return { paddle, loading, openCheckout, openInlineCheckout };
}
